import React, {
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Breadcrumbs } from "../../AbstractElements";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { Table, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { getSaleInfo, getReturnInfo, getPresPreSignedUrl } from "./SalesService";
// import { useReactToPrint } from "react-to-print";
import { ArrowLeft } from "react-feather";
import { useCurrency } from "../../_helper/CurrencyContext";
import { toast } from "react-toastify";
import InvoiceModal from "./NewSales/components/InvoiceModal";
import { buildInvoiceFromSale } from "./utils/buildInvoiceFromSale";
import { formatDate as formatPosDate } from "./NewSales/utils/dateUtils";
import { shouldShowSaleTaxUi } from "../PharmacyManageTax/taxConfigUtils";
import { FaLocationDot } from "react-icons/fa6";
import { TfiEmail } from "react-icons/tfi";
import { FiPhoneCall } from "react-icons/fi";
import { FaUserMd, FaFileMedical, FaHospital, FaRegStickyNote } from "react-icons/fa";
import "../PharmacyLedger/AddNewPatient.scss";
// import { secureApiRequest } from "../../Services/secureApiRequest";
// import { decryptResponseData } from "../../Services/decryptApiResponse";

// Helper function to safely format numbers
const safeToFixed = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return "0.00";
  const num = Number(value);
  if (isNaN(num)) return "0.00";
  return num.toFixed(decimals);
};

// Helper function to safely convert to number
const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const formatDoctorPhoneDisplay = (doctor = {}) => {
  const phoneNumber = String(doctor.phoneNumber || doctor.phone || "").trim();
  if (!phoneNumber) return "";
  const phoneCode = String(doctor.phoneCode || "").trim();
  return phoneCode ? `${phoneCode} ${phoneNumber}`.trim() : phoneNumber;
};

const DoctorDetailRow = ({ icon: Icon, children }) => {
  if (!children) return null;
  return (
    <div className="col-md-12">
      <div className="d-flex align-items-center">
        <div className="p-0 rounded-circle me-2">
          <Icon />
        </div>
        <span style={{ fontSize: "12px" }}>{children}</span>
      </div>
    </div>
  );
};

// ✅ Printable section wrapped with forwardRef
const PrintableContent = forwardRef(({ sale, t, formatDate, taxSummary, showSaleTax = true }, ref) => {
  const { getCurrencySymbol, currency, convertCurrency } = useCurrency();

  // Reconstruct taxable values from stored subTotal and taxPercentage,
  // to match how the backend originally calculated discount/tax.
  const totalTaxableAmt = sale.items?.length
    ? sale.items.reduce((sum, item) => {
      const subTotal = safeNumber(item.subTotal); // inclusive of tax
      const rate = safeNumber(item.taxPercentage);
      if (rate <= 0) return sum + subTotal;
      const taxable = subTotal / (1 + rate / 100);
      return sum + taxable;
    }, 0)
    : 0;

  const discountAmount =
    sale.discountType === "percentage"
      ? (totalTaxableAmt * safeNumber(sale.discount)) / 100
      : safeNumber(sale.discount);

  const netTaxableAmt = totalTaxableAmt - discountAmount;
  const lineSubtotal = sale.items?.length
    ? sale.items.reduce((sum, item) => sum + safeNumber(item.totalPrice || item.subTotal), 0)
    : 0;

  return (
    <div ref={ref}>
      <Row className="mb-4">
        <Col md="8">
          <h4 className="mb-1 fw-bold">
            {t("Invoice No")}: {sale.salesNumber}&nbsp;
          </h4>
          <div>
            {t("Invoice Date")}: {formatDate(sale.createdAt)}
          </div>
          <div>
            {t("Payment Type")}: {sale.paymentType}
          </div>
          <div>
            {t("Sale Type")}:&nbsp;
            {sale.salesType === "return" ? (
              <span className="badge bg-danger text-uppercase">
                {sale.salesType}
              </span>
            ) : (
              <span className="badge bg-success text-uppercase">
                {sale.salesType}
              </span>
            )}
          </div>
          {showSaleTax && taxSummary && (
            <div className="mt-2">
              <Badge bg={sale.isIGST ? "info" : "primary"}>
                {sale.isIGST ? "IGST (Inter-state)" : "CGST + SGST (Intra-state)"}
              </Badge>
            </div>
          )}
        </Col>
        <Col md="4" className="text-end">
          <h5 className="fw-bold">
            {t("Patient")}: {sale.patientName}
          </h5>
          {sale.doctor?.name && (
            <div className="small text-muted mt-1">
              {t("Prescribed Doctor")}: <strong className="text-body">{sale.doctor.name}</strong>
            </div>
          )}
        </Col>
      </Row>

      {/* Items Table */}
      <div className="mb-4 table-responsive">
        <Table bordered hover size="sm">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>{t("Brand Name")}</th>
              <th>{t("Salt Name / Composition")}</th>
              <th>{t("Batch No")}</th>
              <th className="text-center">{t("Qty")}</th>
              <th className="text-center">{t("Unit Price")}</th>
              <th className="text-center">{t("Sale Price")}</th>
              {showSaleTax && (
                <>
                  <th className="text-center">{t("Taxable Amount")}</th>
                  <th className="text-center">{t("Tax %")}</th>
                  {sale.isIGST ? (
                    <th className="text-center">{t("IGST Amt")}</th>
                  ) : (
                    <>
                      <th className="text-center">{t("CGST Amt")}</th>
                      <th className="text-center">{t("SGST Amt")}</th>
                    </>
                  )}
                  <th className="text-center">{t("Total Tax")}</th>
                </>
              )}
              <th className="text-center">{t("Total")}</th>
            </tr>
          </thead>
          <tbody>
            {sale.items.map((item, index) => {
              // Safely convert all tax values to numbers
              const cgst = safeNumber(item.CGST);
              const sgst = safeNumber(item.SGST);
              const igst = safeNumber(item.IGST);
              const tax = safeNumber(item.tax);
              const unitPrice = safeNumber(item.unitPrice);
              const unitPricePerMedicine = safeNumber(item.unitPricePerMedicine);
              const totalPrice = safeNumber(item.totalPrice);
              const taxPercentage = safeNumber(item.taxPercentage);
              const subTotal = safeNumber(item.subTotal);

              const taxableAmount = subTotal / (1 + taxPercentage / 100);
              const discountAmount = safeNumber(item.discount) > 0 ? taxableAmount * (safeNumber(item.discount) / 100) : 0;
              const netTaxableAmount = taxableAmount - discountAmount;
              // console.log(taxableAmount.toFixed(2)); 
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.brandName}</td>
                  <td>{item.medicineName}</td>
                  <td>{item.batchNumber}</td>
                  <td className="text-center">{item.medicineQunatity}</td>
                  <td className="text-center">
                    {getCurrencySymbol()}{safeToFixed(unitPrice)}
                  </td>
                  <td className="text-center">
                    {getCurrencySymbol()}{safeToFixed(unitPricePerMedicine)}
                  </td>
                  {showSaleTax && (
                    <>
                      <td className="text-center">
                        {getCurrencySymbol()}{safeToFixed(netTaxableAmount)}
                      </td>
                      <td className="text-center">{safeToFixed(taxPercentage, 0)}%</td>
                      {sale.isIGST ? (
                        <td className="text-center text-success">
                          {getCurrencySymbol()}{safeToFixed(igst)}
                        </td>
                      ) : (
                        <>
                          <td className="text-center text-success">
                            {getCurrencySymbol()}{safeToFixed(cgst)}
                          </td>
                          <td className="text-center text-success">
                            {getCurrencySymbol()}{safeToFixed(sgst)}
                          </td>
                        </>
                      )}
                      <td className="text-center text-success fw-bold">
                        {getCurrencySymbol()}{safeToFixed(tax)}
                      </td>
                    </>
                  )}
                  <td className="text-center fw-bold">
                    {getCurrencySymbol()}{safeToFixed(totalPrice)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Row
      >
        {/* Tax Summary Section */}
        <Col md="12" lg="6">
          {showSaleTax && taxSummary && taxSummary.length > 0 && (
            <>
              <h5 className="fw-bold text-primary mb-3">{t("Tax Summary")}</h5>
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>{t("GST %")}</th>
                    <th className="text-end">{t("Taxable Value")}</th>
                    {safeNumber(sale.discount) > 0 && (
                      <th className="text-end">{t("Taxable Value (Discounted)")}</th>
                    )}
                    {sale.isIGST ? (
                      <th className="text-end">{t("IGST Amt")}</th>
                    ) : (
                      <>
                        <th className="text-end">{t("CGST Amt")}</th>
                        <th className="text-end">{t("SGST Amt")}</th>
                      </>
                    )}
                    <th className="text-end">{t("Total Tax")}</th>
                    <th className="text-end">{t("Net Amt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {taxSummary.map((taxGroup, index) => (
                    <tr key={index}>
                      <td className="text-center fw-bold">{safeToFixed(taxGroup.rate, 0)}%</td>
                      <td className="text-end">
                        {getCurrencySymbol()}
                        {safeToFixed(taxGroup.taxableBeforeDiscount)}
                      </td>
                      {safeNumber(sale.discount) > 0 && (
                        <td className="text-end">
                          {getCurrencySymbol()}
                          {safeToFixed(taxGroup.taxableAfterDiscount)}
                        </td>
                      )}
                      {sale.isIGST ? (
                        <td className="text-end text-success">
                          {getCurrencySymbol()}{safeToFixed(taxGroup.igst)}
                        </td>
                      ) : (
                        <>
                          <td className="text-end text-success">
                            {getCurrencySymbol()}{safeToFixed(taxGroup.cgst)}
                          </td>
                          <td className="text-end text-success">
                            {getCurrencySymbol()}{safeToFixed(taxGroup.sgst)}
                          </td>
                        </>
                      )}
                      <td className="text-end text-success fw-bold">
                        {getCurrencySymbol()}{safeToFixed(taxGroup.totalTax)}
                      </td>
                      <td className="text-end fw-bold">
                        {getCurrencySymbol()}{safeToFixed(taxGroup.netAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
        {/* Totals Section */}
        <Col md="12" lg="6">
          <Table bordered size="sm">
            <tbody>
              <tr>
                <td className="fw-bold">
                  {showSaleTax ? t("Total Taxable Value") : t("Subtotal")}
                </td>
                <td className="text-end">
                  {getCurrencySymbol()}
                  {safeToFixed(showSaleTax ? totalTaxableAmt : lineSubtotal)}
                </td>
              </tr>

              {safeNumber(sale.discount) > 0 && (
                <>
                  <tr>
                    <td className="fw-bold">
                      {t("Discount")}
                      {sale.discountType === "percentage"
                        ? ` (${safeToFixed(sale.discount, 0)}%)`
                        : " (Fixed)"}
                    </td>
                    <td className="text-end text-danger">
                      - {getCurrencySymbol()}{discountAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">
                      {showSaleTax ? t("Net Taxable Value") : t("Net Amount")}
                    </td>
                    <td className="text-end">
                      {getCurrencySymbol()}
                      {safeToFixed(
                        showSaleTax
                          ? netTaxableAmt
                          : lineSubtotal - discountAmount
                      )}
                    </td>
                  </tr>
                </>
              )}

              {showSaleTax && (
                <>
                  {sale.isIGST ? (
                    <tr>
                      <td className="fw-bold">
                        {t("Add: IGST")} ({safeToFixed(sale.IGSTPercentage, 0)}%)
                      </td>
                      <td className="text-end text-success">
                        + {getCurrencySymbol()}{safeToFixed(sale.IGST)}
                      </td>
                    </tr>
                  ) : (
                    <>
                      <tr>
                        <td className="fw-bold">
                          {t("Add: CGST")} ({safeToFixed(sale.CGSTPercentage, 0)}%)
                        </td>
                        <td className="text-end text-success">
                          + {getCurrencySymbol()}{safeToFixed(sale.CGST)}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">
                          {t("Add: SGST")} ({safeToFixed(sale.SGSTPercentage, 0)}%)
                        </td>
                        <td className="text-end text-success">
                          + {getCurrencySymbol()}{safeToFixed(sale.SGST)}
                        </td>
                      </tr>
                    </>
                  )}

                  <tr className="fw-bold">
                    <td>{t("Total Tax")}</td>
                    <td className="text-end text-success">
                      {getCurrencySymbol()}{safeToFixed(sale.tax)}
                    </td>
                  </tr>
                </>
              )}

              <tr className="fw-bold">
                <td>{t("Paid in")}</td>
                <td className="text-end">{sale.currencyType}</td>
              </tr>
              <tr className="table-primary fw-bold fs-5">
                <td>{t("Grand Total")}</td>
                <td className="text-end">
                  <div className="d-flex flex-column">
                    {currency !== sale.currencyType && (
                      <span className="tw-text-xs text-muted">
                        {getCurrencySymbol()}{safeToFixed(convertCurrency(sale.netTotal, sale.currencyType, currency))}
                      </span>
                    )}
                    {getCurrencySymbol(sale.currencyType)}{safeToFixed(sale.netTotal)}
                  </div>
                </td>
              </tr>

              <tr className="table-success fw-bold">
                <td>{t("Paid")}</td>
                <td className="text-end">
                  {sale.currencyType === "USD" ? "$" : ""}
                  {safeToFixed(sale.totalPaid)}
                  {sale.currencyType === "ZiG" ? " ZiG" : ""}
                </td>
              </tr>

              {sale.salesType === "sales" && safeNumber(sale.dueAmount) !== 0 && (
                <tr className="table-warning fw-bold">
                  <td>{t("Due Amount")}</td>
                  <td className="text-end">
                    {getCurrencySymbol()}{safeToFixed(sale.dueAmount)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  )
});

const ViewSale = () => {
  const { t } = useTranslation();
  const { saleId } = useParams();
  const { getCurrencySymbol } = useCurrency();
  const [sale, setSale] = useState(null);
  const [returnedPurchases, setReturnedPurchases] = useState([]);
  const { setLoading } = useLoader();
  const { pharmacyPOS } = usePharmacy();
  const navigate = useNavigate();

  const printRef = useRef();
  const [showPosInvoice, setShowPosInvoice] = useState(false);
  const [openingPrescription, setOpeningPrescription] = useState(false);

  const invoiceData = useMemo(
    () => (sale ? buildInvoiceFromSale(sale, getCurrencySymbol) : null),
    [sale, getCurrencySymbol]
  );

  const doctorPhone = useMemo(
    () => (sale?.doctor ? formatDoctorPhoneDisplay(sale.doctor) : ""),
    [sale?.doctor]
  );

  const hasDoctorSection = Boolean(
    sale?.doctor?.name ||
    sale?.prescriptionDate ||
    sale?.note ||
    sale?.prescription
  );

  // Print Details disabled — use POS Receipt instead
  // const handlePrint = useReactToPrint({
  //   content: () => printRef.current,
  //   documentTitle: `Invoice_${sale?.salesNumber}`,
  //   pageStyle: `
  // 		.receipt-container {
  // 			padding: 10px 5px;
  // 		}
  //   `,
  // });

  const printPosInvoice = () => {
    const invoiceContent = document?.getElementById("invoice-content");
    if (!invoiceContent) return;
    const printWindow = window.open("", "", "width=400,height=600");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <style>
            @page { size: 80mm auto; margin: 5mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; width: 80mm; margin: 0 auto; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            .bold { font-weight: bold; }
            .dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            td { padding: 2px 0; vertical-align: top; }
            .section { margin-top: 6px; margin-bottom: 6px; }
            .thankyou { margin-top: 12px; font-size: 12px; text-align: center; }
          </style>
        </head>
        <body>${invoiceContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  useEffect(() => {
    const fetchSale = async () => {
      try {
        setLoading(true);
        const body = {
          clinicId: pharmacyPOS?.hospital_id,
          salesId: saleId,
        };
        const saleReturnPayload = {
          salesId: saleId,
        }
        const response = await getSaleInfo(body);
        const saleReturnResponse = await getReturnInfo(saleReturnPayload);
        const data = saleReturnResponse.data.data;
        setReturnedPurchases(data.sales || []);
        if (response.data?.status === "success") {
          setSale(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching sale details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSale();
  }, [saleId]);

  // Calculate tax summary grouped by tax percentage
  const taxSummary = useMemo(() => {
    if (!sale?.items) return [];

    // Group items by tax percentage
    const taxGroups = sale.items.reduce((acc, item) => {
      const rate = safeNumber(item.taxPercentage);
      if (!acc[rate]) {
        acc[rate] = {
          rate,
          taxableBeforeDiscount: 0,
          taxableAfterDiscount: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          totalTax: 0,
          netAmount: 0
        };
      }

      const subTotal = safeNumber(item.subTotal); // inclusive line total
      const totalPrice = safeNumber(item.totalPrice);
      const tax = safeNumber(item.tax);
      const rateValue = rate > 0 ? rate : 0;

      // Taxable before any bill-level discount (derived from subTotal)
      const taxableBefore =
        rateValue > 0 ? subTotal / (1 + rateValue / 100) : subTotal;

      const cgst = safeNumber(item.CGST);
      const sgst = safeNumber(item.SGST);
      const igst = safeNumber(item.IGST);

      acc[rate].taxableBeforeDiscount += taxableBefore;
      acc[rate].cgst += cgst;
      acc[rate].sgst += sgst;
      acc[rate].igst += igst;
      acc[rate].totalTax += tax;
      acc[rate].netAmount += totalPrice;

      return acc;
    }, {});

    const groups = Object.values(taxGroups);

    // Distribute bill-level discount across tax groups based on
    // their share of the taxable-before-discount.
    const totalTaxableBefore = groups.reduce(
      (sum, g) => sum + g.taxableBeforeDiscount,
      0
    );

    const discountAmount =
      sale.discountType === "percentage"
        ? (totalTaxableBefore * safeNumber(sale.discount)) / 100
        : safeNumber(sale.discount);

    groups.forEach((g) => {
      const proportion =
        totalTaxableBefore > 0 ? g.taxableBeforeDiscount / totalTaxableBefore : 0;
      const groupDiscount = discountAmount * proportion;
      g.taxableAfterDiscount = Math.max(
        0,
        g.taxableBeforeDiscount - groupDiscount
      );
    });

    // Convert to array and sort by rate
    return groups.sort((a, b) => a.rate - b.rate);
  }, [sale]);

  const showSaleTax = useMemo(
    () =>
      shouldShowSaleTaxUi(pharmacyPOS, {
        saleTax: sale?.tax,
        items: sale?.items || [],
      }),
    [pharmacyPOS, sale]
  );

  if (!sale) {
    return <div className="py-5 text-center">{t("Loading...")}</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format returned purchases items with proper tax display
  const formatReturnItems = (returnSale) => {
    return returnSale.items?.map(item => ({
      ...item,
      // Ensure tax values are properly converted to numbers
      tax: safeNumber(item.tax),
      taxPercentage: safeNumber(item.taxPercentage),
      isIGST: item.isIGST || false,
      IGST: safeNumber(item.IGST),
      CGST: safeNumber(item.CGST),
      SGST: safeNumber(item.SGST)
    })) || [];
  };

  // const extractS3Key = (url) => {
  //   if (!url) return "";
  //   return url.split(".com/")[1]; // UserPrescription/xxx.pdf
  // };


  const getPrescriptionPresignedURL = async () => {
    const key = sale?.prescription;
    if (!key) return;

    try {
      setOpeningPrescription(true);
      setLoading(true);

      const response = await getPresPreSignedUrl({ key });
      const url = response?.data?.data?.presignedURL;

      if (!url) {
        toast.warning(t("Prescription URL not available"));
        return;
      }

      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) {
        toast.warning(t("Please allow pop-ups to view the prescription"));
      }
    } catch (err) {
      console.error("Failed to load prescription", err);
      toast.warning(t("Failed to load prescription"));
    } finally {
      setOpeningPrescription(false);
      setLoading(false);
    }
  };


  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("View Sale")}
        parent={t("Dashboard")}
        title={t("View Sale")}
      />

      <div className="gap-2 mb-3 d-flex justify-content-end">
        <Button
          color="primary"
          size="sm"
          className="d-flex align-items-center justify-content-center"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/sales-history`)}
        >
          <ArrowLeft size={16} className="me-1" />
          {t("Back to Sale History")}
        </Button>
        {/* <Button color="primary" onClick={handlePrint}>
          <i className="fa fa-print"></i> {t("Print Details")}
        </Button> */}
        {invoiceData && (
          <Button color="success" onClick={() => setShowPosInvoice(true)}>
            <i className="fa fa-receipt me-1"></i> {t("POS Receipt")}
          </Button>
        )}
      </div>

      {hasDoctorSection && (
        <Col sm="12" className="mb-3">
          <Card className="patient-card-background mb-0">
            <div className="p-2">
              <div className="p-2 d-flex flex-wrap align-items-start tw-space-x-2">
                <div
                  className="patient-pic img-60 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ fontSize: "1.25rem", fontWeight: 700 }}
                  aria-hidden="true"
                >
                  <FaUserMd />
                </div>

                <div className="ps-3 flex-grow-1" style={{ minWidth: "180px" }}>
                  <h6 className="mb-0">
                    <strong>{sale.doctor?.name || t("Prescription Details")}</strong>
                  </h6>
                  {(sale.doctor?.qualification || sale.doctor?.speciality) && (
                    <small style={{ fontSize: "12px", textTransform: "capitalize" }}>
                      {[sale.doctor?.qualification, sale.doctor?.speciality]
                        .filter(Boolean)
                        .join(", ")}
                    </small>
                  )}
                  {sale.doctor?.registrationNumber && (
                    <div style={{ fontSize: "12px", opacity: 0.9 }}>
                      {t("Reg. No")}: {sale.doctor.registrationNumber}
                    </div>
                  )}
                  {sale.prescriptionDate && (
                    <small style={{ fontSize: "12px" }}>
                      {t("Rx Date")}: {formatPosDate(sale.prescriptionDate)}
                    </small>
                  )}
                </div>

                <div className="row ps-3 flex-grow-1" style={{ minWidth: "220px" }}>
                  <DoctorDetailRow icon={FiPhoneCall}>{doctorPhone}</DoctorDetailRow>
                  <DoctorDetailRow icon={TfiEmail}>{sale.doctor?.email}</DoctorDetailRow>
                  <DoctorDetailRow icon={FaHospital}>{sale.doctor?.clinicName}</DoctorDetailRow>
                  <DoctorDetailRow icon={FaLocationDot}>{sale.doctor?.clinicAddress}</DoctorDetailRow>
                  {(sale.note || sale.doctor?.note) && (
                    <DoctorDetailRow icon={FaRegStickyNote}>
                      {sale.note || sale.doctor?.note}
                    </DoctorDetailRow>
                  )}
                </div>

                {sale.prescription && (
                  <Button
                    color="warning"
                    size="sm"
                    className="ms-auto flex-shrink-0 align-self-center"
                    disabled={openingPrescription}
                    onClick={getPrescriptionPresignedURL}
                  >
                    <FaFileMedical className="me-1" />
                    {openingPrescription ? t("Opening...") : t("View Prescription")}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Col>
      )}

      <Col sm="12">
        <Card>
          <CardBody className="receipt-container">
            {/* ✅ Now using forwardRef wrapped component */}
            <PrintableContent
              ref={printRef}
              sale={sale}
              t={t}
              formatDate={formatDate}
              taxSummary={taxSummary}
              showSaleTax={showSaleTax}
            />

            {/* Returned Purchases */}
            {returnedPurchases.length > 0 && (
              <div className="mt-5">
                <h5 className="fw-bold text-danger mb-4">{t("Return History")}</h5>

                {returnedPurchases.map((ret) => {
                  const isReturnIGST = ret.isIGST || false;
                  const formattedItems = formatReturnItems(ret);
                  const showReturnTax = shouldShowSaleTaxUi(pharmacyPOS, {
                    saleTax: ret.tax,
                    items: ret.items || [],
                  });

                  // Reconstruct taxable values and discount for each return sale
                  const retTotalTaxable = ret.items?.length
                    ? ret.items.reduce((sum, item) => {
                        const subTotal = safeNumber(item.subTotal); // inclusive line total
                        const rate = safeNumber(item.taxPercentage);
                        if (rate <= 0) return sum + subTotal;
                        const taxable = subTotal / (1 + rate / 100);
                        return sum + taxable;
                      }, 0)
                    : 0;

                  return (
                    <Card className="mb-4 border-danger" key={ret._id}>
                      <CardBody>
                        {/* Top section with return details */}
                        <Row className="mb-3">
                          <Col md="8">
                            <div className="d-flex align-items-center gap-2">
                              <strong className="text-danger">{t("Return No")}:</strong>
                              <span>{ret.salesNumber}</span>
                              <Badge bg="danger">{t("RETURN")}</Badge>
                              {showReturnTax && (
                                <Badge bg={isReturnIGST ? "info" : "primary"}>
                                  {isReturnIGST ? "IGST" : "CGST+SGST"}
                                </Badge>
                              )}
                            </div>
                            <div>
                              <strong>{t("Return Date")}:</strong> {formatDate(ret.createdAt)}
                            </div>
                            <div>
                              <strong>{t("Customer")}:</strong> {ret.patientName}
                            </div>
                          </Col>

                          <Col md="4" className="text-end">
                            <div className="text-danger fw-bold fs-5">
                              {t("Total Return Amount")}: {getCurrencySymbol()}{safeToFixed(ret.netTotal)}
                            </div>
                            {showReturnTax && (
                              <div className="text-muted small">
                                {t("Tax")}: {getCurrencySymbol()}{safeToFixed(ret.tax)}
                              </div>
                            )}
                          </Col>
                        </Row>

                        {/* Items Table */}
                        <div className="table-responsive mb-3">
                          <Table bordered size="sm" className="align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>{t("Brand Name")}</th>
                                <th>{t("Batch No")}</th>
                                <th className="text-center">{t("Returned Qty")}</th>
                                <th className="text-center">{t("Sale Price")}</th>
                                {showReturnTax && (
                                  <>
                                    <th className="text-center">{t("Tax %")}</th>
                                    {isReturnIGST ? (
                                      <th className="text-center">{t("IGST Amt")}</th>
                                    ) : (
                                      <>
                                        <th className="text-center">{t("CGST Amt")}</th>
                                        <th className="text-center">{t("SGST Amt")}</th>
                                      </>
                                    )}
                                    <th className="text-center">{t("Total Tax")}</th>
                                  </>
                                )}
                                <th className="text-center">{t("Total")}</th>
                              </tr>
                            </thead>

                            <tbody>
                              {formattedItems.map((item, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>{item.brandName || item.medicineName || "-"}</td>
                                  <td>{item.batchNumber}</td>
                                  <td className="text-center">{item.medicineQunatity}</td>
                                  <td className="text-center">
                                    {getCurrencySymbol()}{safeToFixed(item.unitPricePerMedicine)}
                                  </td>
                                  {showReturnTax && (
                                    <>
                                      <td className="text-center">{safeToFixed(item.taxPercentage, 0)}%</td>
                                      {isReturnIGST ? (
                                        <td className="text-center text-success">
                                          {getCurrencySymbol()}{safeToFixed(item.IGST)}
                                        </td>
                                      ) : (
                                        <>
                                          <td className="text-center text-success">
                                            {getCurrencySymbol()}{safeToFixed(item.CGST)}
                                          </td>
                                          <td className="text-center text-success">
                                            {getCurrencySymbol()}{safeToFixed(item.SGST)}
                                          </td>
                                        </>
                                      )}
                                      <td className="text-center text-success fw-bold">
                                        {getCurrencySymbol()}{safeToFixed(item.tax)}
                                      </td>
                                    </>
                                  )}
                                  <td className="text-center fw-bold text-danger">
                                    {getCurrencySymbol()}{safeToFixed(item.totalPrice)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        {/* Return Summary */}
                        <Row className="align-items-end">
                          {showReturnTax && (
                          <Col md="8">
                            <Table bordered size="sm" className="mb-3">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center">{t("Summary")}</th>
                                  <th className="text-end">{t("Taxable Amount")}</th>
                                  {isReturnIGST ? (
                                    <th className="text-end">{t("IGST")}</th>
                                  ) : (
                                    <>
                                      <th className="text-end">{t("CGST")}</th>
                                      <th className="text-end">{t("SGST")}</th>
                                    </>
                                  )}
                                  <th className="text-end">{t("Total Tax")}</th>
                                  <th className="text-end">{t("Net Amount")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="text-center fw-bold">{t("Return Summary")}</td>
                                  <td className="text-end">
                                    {getCurrencySymbol()}{safeToFixed(retTotalTaxable)}
                                  </td>
                                  {isReturnIGST ? (
                                    <td className="text-end text-success">
                                      {getCurrencySymbol()}{safeToFixed(ret.IGST)}
                                    </td>
                                  ) : (
                                    <>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}{safeToFixed(ret.CGST)}
                                      </td>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}{safeToFixed(ret.SGST)}
                                      </td>
                                    </>
                                  )}
                                  <td className="text-end fw-bold text-success">
                                    {getCurrencySymbol()}{safeToFixed(ret.tax)}
                                  </td>
                                  <td className="text-end fw-bold text-danger">
                                    {getCurrencySymbol()}{safeToFixed(ret.netTotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                          )}
                          <Col md={showReturnTax ? 4 : 12}>
                            <Table bordered size="sm">
                              <tbody>
                                {showReturnTax &&
                                  (isReturnIGST ? (
                                    <tr>
                                      <td className="text-end">{t("IGST")}:</td>
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol()}{safeToFixed(ret.IGST)}
                                      </td>
                                    </tr>
                                  ) : (
                                    <>
                                      <tr>
                                        <td className="text-end">{t("CGST")}:</td>
                                        <td className="text-end text-success fw-bold">
                                          {getCurrencySymbol()}{safeToFixed(ret.CGST)}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="text-end">{t("SGST")}:</td>
                                        <td className="text-end text-success fw-bold">
                                          {getCurrencySymbol()}{safeToFixed(ret.SGST)}
                                        </td>
                                      </tr>
                                    </>
                                  ))}
                                <tr className="table-danger fw-bold fs-5">
                                  <td className="text-end">{t("RETURN TOTAL")}</td>
                                  <td className="text-end text-danger">
                                    {getCurrencySymbol()}{safeToFixed(ret.netTotal)}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      <InvoiceModal
        t={t}
        show={showPosInvoice}
        invoiceData={invoiceData}
        pharmacyData={pharmacyPOS}
        onHide={() => setShowPosInvoice(false)}
        onClose={() => setShowPosInvoice(false)}
        onPrint={printPosInvoice}
      />
    </Fragment>
  );
};

export default ViewSale;